import { skipToken } from '@reduxjs/toolkit/query';
import { useCreateDialogChip, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import { format } from 'date-fns';
import useAuth from 'hooks/useAuth.ts';
import { mapToContactMinDto } from 'pages/contacts/util.tsx';
import { useCallback, useEffect, useState } from 'react';
import { useGetContactByIdQuery } from 'store/api/contacts';
import { useGetEmployeeByIdQuery } from 'store/api/employees';
import { useFindMatterRatesQuery, useGetMatterByIdQuery } from 'store/api/matters';
import { useGetOfficeByIdQuery } from 'store/api/offices';
import { useGetTenantConfigurationQuery } from 'store/api/tenants.ts';
import { PartyType, RelationshipTypeCode } from 'types/contacts';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features.ts';
import {
    EngagementLengthUnit,
    MatterAddPrefill,
    MatterBlockingPolicyType,
    MatterDto,
    MatterForm,
    MatterPartyRoleType,
    MatterStateType,
    OfferValueType,
    PricingLimitationType,
    RemunerationCalcType,
} from 'types/matters';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getClassifierOptionByCode, getOptionsFromClassifier } from 'utils/formUtil';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { toTimecardIncrementOption } from 'utils/timecardIncrementOptions';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { baseTabWithRates } from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { isEmployeePriced } from 'pages/pricelists/modal/employeePricing';
import MatterPageForm from './MatterPageForm';
import { copiedPayerRows, emptyPrimaryPayerRow } from './matterPayers';
import { copiedPricingLink, seedCopiedPricing } from './matterPricingCopy';
import { copiedPracticeAreas, reduceParties, splitCaseStaff } from './matterUtils';
import { useInactivePricedPeople } from './useInactivePricedPeople';
import { useSettings } from 'utils/useSettings';
import { Setting } from 'types/back-office-settings';

const USED_CLASSIFIERS = [
    'ACCOUNTING_REVENUE_TYPE',
    'ARCHIVING_PROPERTIES_TYPE',
    'MATTER_STATE_TYPE',
    'MATTER_TYPE',
    'MATTER_BLOCKING_POLICY_TYPE',
    'REASON_OFFER_LOST',
    'OFFER_VALUE_TYPE',
    'ENGAGEMENT_LENGTH_UNIT',
    'LANGUAGE',
    'CURRENCY',
    'PRICING_LIMITATION_TYPE',
    'CAP_UNIT_TYPE',
    'INVOICE_TEMPLATE_TYPE',
    'INVOICING_INTERVAL_TYPE',
    'JOB_ROLE_GROUP_TYPE',
    'CONTRACT_STATUS_TYPE',
];

function readPrefill(state: unknown): MatterAddPrefill | undefined {
    return state && typeof state === 'object' && 'prefill' in state
        ? (state as { prefill: MatterAddPrefill }).prefill
        : undefined;
}

const MatterPageAdd = () => {
    const {
        params: { contactId },
        location: { state: locationState },
    } = useCreateRoute();
    const [initialData, setInitialData] = useState<MatterForm>();
    const { user } = useAuth();
    const userId = user?.id;

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: USED_CLASSIFIERS,
    });

    const isCaseMergeInvoiceSpecEnabled = useIsFeatureEnabled(FeatureFlag.CASE_MERGE_INVOICE_SPEC);
    const isAdministratorFieldEnabled = useSettings().isSettingEnabled(Setting.ADMINISTRATOR_FIELD);
    const isHandledByOfficeControlEnabled = useIsFeatureEnabled(FeatureFlag.HANDLED_BY_OFFICE_CONTROL);
    const canManagePayers = useHasPermissions(['UPDATE.MATTERS.ADD_AND_MANAGE_PAYERS']);
    const isAutofillNoOpponent = useIsFeatureEnabled(FeatureFlag.AUTOFILL_CASE_NO_OPPONENT);
    const isOfficialRefNumberEnabled = useIsFeatureEnabled(FeatureFlag.CASE_OFFICIAL_REF_NUMBER_FIELD);
    const isSinglePracticeArea = useIsFeatureEnabled(FeatureFlag.CASE_PA_FIELD_SINGLE_SELECTION_ONLY);
    // isFetching, not isLoading: a configuration saved moments ago leaves the cache warm and
    // refetching, and locking initialData then would prefill the defaults the save replaced.
    const { data: tenantConfiguration, isFetching: isFetchingTenantConfiguration } = useGetTenantConfigurationQuery();

    const { data: contact, isLoading: isLoadingContact } = useGetContactByIdQuery(contactId ?? skipToken);
    useCreateDialogChip({ parent: contact?.name });

    const sourceMatterId = readPrefill(locationState)?.originatesFromMatterId ?? null;
    // The status change that makes the Case eligible can have happened moments ago, on the save that led here.
    const {
        data: sourceMatterData,
        isError: isSourceMatterError,
        isFetching: isFetchingSourceMatter,
    } = useGetMatterByIdQuery(sourceMatterId !== null ? sourceMatterId.toString() : skipToken, {
        refetchOnMountOrArgChange: true,
    });

    // Router state travels along further navigations, so an Offer Won Case that already produced its one
    // resulting Case — or a Case at any other status — is not allowed to seed another form.
    const sourceMatter =
        sourceMatterData?.matterStateTypeCode === MatterStateType.OFFER_WON && !sourceMatterData.connectedCase
            ? sourceMatterData
            : undefined;

    // An employee-priced offer hands its person rows and dated lists on; a role-priced one its in-force role rows.
    const { data: sourcePricelists, isFetching: isFetchingSourcePricelists } = useFindMatterRatesQuery(
        sourceMatter && isEmployeePriced(sourceMatter) ? String(sourceMatter.id) : skipToken
    );
    const { inactiveEmployeeIds, isLoading: isLoadingActivePricedEmployees } =
        useInactivePricedPeople(sourcePricelists);

    // The prefill silently degrades to a blank form, which would create an unlinked Case the user believes is linked.
    useEffect(() => {
        if (isSourceMatterError) {
            errorAlert(t('matters.error-fetching-matter'));
        }
    }, [isSourceMatterError]);

    const { initialCustomFieldsObject, isLoading: isLoadingCustomFieldData } = useCustomFields({
        entityType: EntityType.MATTER,
        fields: sourceMatter?.customFields,
    });

    const { data: currentEmployeeData, isLoading: isLoadingCurrentEmployee } = useGetEmployeeByIdQuery(
        userId ? userId.toString() : skipToken
    );

    const currentUserOfficeId = currentEmployeeData?.latestEmployment?.officeId;
    const { data: currentUserOffice, isLoading: isLoadingCurrentUserOffice } = useGetOfficeByIdQuery(
        currentUserOfficeId?.toString() ?? skipToken
    );

    const primaryOfficeForCasesId = isHandledByOfficeControlEnabled
        ? tenantConfiguration?.primaryOfficeForCasesId
        : null;
    const { data: primaryOfficeForCases, isLoading: isLoadingPrimaryOffice } = useGetOfficeByIdQuery(
        primaryOfficeForCasesId?.toString() ?? skipToken
    );

    // Set initial form data. Certain fields can be preset if adding under existing contact.
    const getInitialData = useCallback((): MatterForm | undefined => {
        if (classifiers) {
            const officeToUse =
                isHandledByOfficeControlEnabled && primaryOfficeForCases ? primaryOfficeForCases : currentUserOffice;
            const prefillOffice = officeToUse
                ? {
                      ...officeToUse,
                  }
                : null;

            const defaultStateType =
                !contact || contact?.relationshipTypeCode === RelationshipTypeCode.LEAD
                    ? MatterStateType.OFFER_PENDING
                    : MatterStateType.IN_PROGRESS;

            return {
                readOnlyCaseNumber: null,
                name: '',
                clientParty: contact ? mapToContactMinDto(contact) : null,
                matterStateType:
                    (
                        getOptionsFromClassifier(classifiers['MATTER_STATE_TYPE']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === defaultStateType) ?? null,
                matterType: null,
                reasonOfferLost: null,
                outcomeNotes: '',
                offerValueType:
                    (
                        getOptionsFromClassifier(classifiers['OFFER_VALUE_TYPE']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === OfferValueType.CASH) ?? null,
                offerValueLow: '',
                offerValueHigh: '',
                estimatedEngagementLength: '',
                estimatedEngagementUnit:
                    (
                        getOptionsFromClassifier(classifiers['ENGAGEMENT_LENGTH_UNIT']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === EngagementLengthUnit.WEEKS) ?? null,
                originatesFromMatterId: null,
                handledByOffice: prefillOffice,
                generalNotes: '',
                officialReference: '',
                isUsedTimeUsedForRemuneration: false,
                isConfidential: false,
                description: contact ? contact.name : '',
                practiceAreas: [],
                secondLevelPracticeAreas: [],
                dmsPracticeArea: null,
                serviceAgreementContract: null,
                timecardIncrement: null,
                project: null,
                openedDate: format(new Date(), DEFAULT_DATE_FORMAT),
                closedDate: null,
                responsibleEmployee: null,
                supervisedByEmployee: null,
                // The tenant's back-office default for Handled by; still editable on the form. A tenant that keeps it
                // for confidential cases gets it once the case is ticked confidential (useMatterForm).
                employees: tenantConfiguration?.isDefaultHandledByOnlyWhenConfidential
                    ? []
                    : (tenantConfiguration?.defaultHandledByEmployees ?? []),
                administrators: [],
                primaryContactParty:
                    contact && contact.partyType === PartyType.PERSON ? { id: contact.id, name: contact.name } : null,
                representedClients: [],
                isNoOpponent: isAutofillNoOpponent,
                opponentParties: [],
                targetParties: [],
                otherParties: [],
                judgeParties: [],
                archivedInOffice: null,
                archivedDate: null,
                archivingNotes: '',
                archivingPropertiesType: null,
                discountPct: '',
                discountExpiryDate: null,
                isCostIncluded: false,
                pricingLimitationType: null,
                accountingRevenueType: null,
                capUnitType: null,
                capLimit: '',
                fixedFee: '',
                recurringFixedFee: '',
                billingNotes: '',
                invoicingInfo: '',
                timecardLanguage:
                    contact && contact?.preferredLanguageCode
                        ? ((
                              getOptionsFromClassifier(classifiers['LANGUAGE']) as {
                                  label: string;
                                  value: string;
                              }[]
                          ).find((val) => val.value === contact.preferredLanguageCode) ?? null)
                        : null,
                primaryRecipientParty: null,
                primaryRecipientAdditionalEmails: '',
                secondaryRecipientParties: [],
                secondaryRecipientAdditionalEmails: '',
                matterClientReference: '',
                invoiceMatterName: '',
                invoiceMatterNameLocalLanguage: '',
                invoiceTemplate: null,
                invoicedByOffice: null,
                invoicingIntervalType: null,
                payerParty: null,
                payers: [emptyPrimaryPayerRow()],
                invoiceSpecTemplate: null,
                referencedPricelist: null,
                pricelists: [baseTabWithRates()],
                pricelistCurrencyType: null,
                paymentCurrencyType: null,
                isPaymentCurrencyDifferent: false,
                isJointInvoicingDisabled: false,
                isAutomaticClosingExempt: false,
                matterBlockingPolicyType:
                    (
                        getOptionsFromClassifier(classifiers['MATTER_BLOCKING_POLICY_TYPE']) as {
                            label: string;
                            value: string;
                        }[]
                    ).find((val) => val.value === MatterBlockingPolicyType.CLIENT_DEFAULT) ?? null,
                nextInvoicingDate: null,
                paymentNotificationReceivers: [],
                dmsLink: '',
                preserveNonBillableHours: false,
                isAccessibleFirmWide: false,
                isExcludedFromXR: false,
                isPaperFile: false,
                changeReasons: null,
                invoiceCustomization: null,
                isSpecIncludedInInvoice: isCaseMergeInvoiceSpecEnabled,
                isSpecExcludedFromEmail: false,
                isAmlBlockingExempt: false,
                ...initialCustomFieldsObject,
            };
        }

        return undefined;
    }, [
        classifiers,
        currentUserOffice,
        primaryOfficeForCases,
        isHandledByOfficeControlEnabled,
        contact,
        isCaseMergeInvoiceSpecEnabled,
        isAutofillNoOpponent,
        initialCustomFieldsObject,
        tenantConfiguration,
    ]);

    // The Case opened from an Offer Won Case continues that engagement, so it starts out as a copy of it.
    const getInitialDataFromSourceMatter = useCallback(
        (matter: MatterDto): MatterForm => ({
            readOnlyCaseNumber: null,
            name: matter.name,
            clientParty: matter.clientParty ? mapToContactMinDto(matter.clientParty) : null,
            matterStateType:
                (
                    getOptionsFromClassifier(classifiers['MATTER_STATE_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === MatterStateType.IN_PROGRESS) ?? null,
            matterType: getClassifierOptionByCode<string, string>(
                classifiers['MATTER_TYPE'],
                matter.matterTypeCode ?? null
            ),
            // The offer outcome fields are only shown while the Case sits at an offer status, so they stay behind.
            reasonOfferLost: null,
            outcomeNotes: '',
            offerValueType:
                (
                    getOptionsFromClassifier(classifiers['OFFER_VALUE_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === (matter.offerValueTypeCode ?? OfferValueType.CASH)) ?? null,
            offerValueLow: matter.offerValueLow ?? '',
            offerValueHigh: matter.offerValueHigh ?? '',
            estimatedEngagementLength: matter.estimatedEngagementLength ?? '',
            estimatedEngagementUnit:
                (
                    getOptionsFromClassifier(classifiers['ENGAGEMENT_LENGTH_UNIT']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === (matter.estimatedEngagementUnitCode ?? EngagementLengthUnit.WEEKS)) ??
                null,
            originatesFromMatterId: matter.id,
            handledByOffice: matter.handledByOffice ? { ...matter.handledByOffice } : null,
            generalNotes: matter.generalNotes ?? '',
            officialReference: isOfficialRefNumberEnabled ? (matter.officialReference ?? '') : '',
            isUsedTimeUsedForRemuneration: matter.remunerationCalcTypeCode === RemunerationCalcType.EQUALS_USED_TIME,
            isConfidential: matter.isConfidential ?? false,
            description: matter.description?.length
                ? matter.description
                : [matter.clientParty?.name, matter.name].filter(Boolean).join(' '),
            ...copiedPracticeAreas(matter, isSinglePracticeArea),
            serviceAgreementContract: matter.serviceAgreementContract || null,
            timecardIncrement:
                matter.timecardIncrement != null ? toTimecardIncrementOption(matter.timecardIncrement) : null,
            project: matter.project ?? null,
            openedDate: format(new Date(), DEFAULT_DATE_FORMAT),
            closedDate: null,
            responsibleEmployee: matter.responsibleEmployee ?? null,
            supervisedByEmployee: matter.supervisedByEmployee ?? null,
            ...splitCaseStaff(matter.employees ?? [], isAdministratorFieldEnabled),
            primaryContactParty: matter.primaryContactParty
                ? { id: matter.primaryContactParty.id, name: matter.primaryContactParty.name }
                : null,
            representedClients: reduceParties(matter.parties ?? [], MatterPartyRoleType.CLIENT_REPRESENTED),
            isNoOpponent: matter.isNoOpponent ?? false,
            opponentParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.OPPONENT),
            targetParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.TARGET),
            otherParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.OTHER_PARTY),
            judgeParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.JUDGE),
            // A brand-new case is never already archived, and its invoicing schedule starts fresh.
            archivedInOffice: null,
            archivedDate: null,
            archivingNotes: '',
            archivingPropertiesType: null,
            discountPct: matter.discountPct ?? '',
            discountExpiryDate: matter.discountExpiryDate
                ? formatDateStringInput(new Date(matter.discountExpiryDate))
                : null,
            isCostIncluded: matter.isCostIncluded ?? false,
            // Non-billable on an offer Case is the Lead-driven default rather than a decision about the
            // engagement, so the new Case asks for an explicit fee arrangement instead of inheriting it.
            pricingLimitationType:
                matter.pricingLimitationTypeCode === PricingLimitationType.NON_BILLABLE
                    ? null
                    : ((
                          getOptionsFromClassifier(classifiers['PRICING_LIMITATION_TYPE']) as {
                              label: string;
                              value: string;
                          }[]
                      ).find((val) => val.value === matter.pricingLimitationTypeCode) ?? null),
            accountingRevenueType:
                (
                    getOptionsFromClassifier(classifiers['ACCOUNTING_REVENUE_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === matter.accountingRevenueTypeCode) ?? null,
            capUnitType:
                (
                    getOptionsFromClassifier(classifiers['CAP_UNIT_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === matter.capUnitTypeCode) ?? null,
            capLimit: matter.capLimit ?? '',
            fixedFee: matter.fixedFee ?? '',
            recurringFixedFee: matter.recurringFixedFee ?? '',
            billingNotes: matter.billingNotes ?? '',
            invoicingInfo: matter.invoicingInfo ?? '',
            timecardLanguage:
                (
                    getOptionsFromClassifier(classifiers['LANGUAGE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === matter.timecardLanguageCode) ?? null,
            primaryRecipientParty: matter.primaryRecipientParty
                ? {
                      id: matter.primaryRecipientParty.id,
                      name: matter.primaryRecipientParty.name,
                      email: matter.primaryRecipientParty.mainEmail || '',
                  }
                : null,
            primaryRecipientAdditionalEmails: matter.primaryRecipientAdditionalEmails ?? '',
            secondaryRecipientParties: (matter.parties ?? [])
                .filter(
                    ({ matterPartyRoleTypeCode }) => matterPartyRoleTypeCode === MatterPartyRoleType.SECONDARY_RECIPIENT
                )
                .map(({ party }) => ({ id: party.id, name: party.name, email: party.mainEmail })),
            secondaryRecipientAdditionalEmails: matter.secondaryRecipientAdditionalEmails ?? '',
            matterClientReference: matter.matterClientReference ?? '',
            // The stored name is built from the source Case number, which the new Case does not have yet.
            invoiceMatterName: matter.name,
            invoiceMatterNameLocalLanguage: matter.invoiceMatterNameLocalLanguage ?? '',
            invoiceTemplate: matter.invoiceTemplate
                ? { id: matter.invoiceTemplate.id, name: matter.invoiceTemplate.name }
                : null,
            invoicedByOffice: matter.invoicedByOffice ? { ...matter.invoicedByOffice } : null,
            invoicingIntervalType:
                (
                    getOptionsFromClassifier(classifiers['INVOICING_INTERVAL_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === matter.invoicingIntervalTypeCode) ?? null,
            payerParty: matter.payerParty,
            payers: copiedPayerRows(matter, canManagePayers),
            invoiceSpecTemplate: matter.invoiceSpecTemplate
                ? { id: matter.invoiceSpecTemplate.id, name: matter.invoiceSpecTemplate.name }
                : null,
            referencedPricelist:
                matter.pricelist || matter.pricingHash
                    ? { id: matter.pricelist?.id || null, pricingHash: matter.pricingHash }
                    : null,
            // A role-priced offer's in-force role rows seed the base tab only, without personal rows or dated lists.
            pricelists: isEmployeePriced(matter)
                ? seedCopiedPricing(sourcePricelists ?? [], format(new Date(), DEFAULT_DATE_FORMAT))
                : [baseTabWithRates(matter.rates ?? [])],
            ...copiedPricingLink(matter),
            pricelistCurrencyType:
                (
                    getOptionsFromClassifier(classifiers['CURRENCY']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === matter.pricelistCurrencyTypeCode) ?? null,
            paymentCurrencyType:
                (
                    getOptionsFromClassifier(classifiers['CURRENCY']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === matter.paymentCurrencyTypeCode) ?? null,
            isPaymentCurrencyDifferent:
                !!matter.paymentCurrencyTypeCode && matter.paymentCurrencyTypeCode !== matter.pricelistCurrencyTypeCode,
            isJointInvoicingDisabled: matter.isJointInvoiceDisabled ?? false,
            isAutomaticClosingExempt: matter.isAutomaticClosingExempt ?? false,
            matterBlockingPolicyType:
                (
                    getOptionsFromClassifier(classifiers['MATTER_BLOCKING_POLICY_TYPE']) as {
                        label: string;
                        value: string;
                    }[]
                ).find((val) => val.value === matter.matterBlockingPolicyTypeCode) ?? null,
            nextInvoicingDate: null,
            paymentNotificationReceivers: matter.paymentNotificationReceivers?.map((item) => item.employee) ?? [],
            // A DMS link points at the source Case's own folder.
            dmsLink: null,
            preserveNonBillableHours: matter.preserveNonBillableHours,
            isAccessibleFirmWide: matter.isAccessibleFirmWide ?? false,
            isExcludedFromXR: matter.isExcludedFromXR,
            isPaperFile: matter.isPaperFile,
            changeReasons: null,
            invoiceCustomization: matter.invoiceCustomization,
            isSpecIncludedInInvoice: matter.isSpecIncludedInInvoice ?? false,
            isSpecExcludedFromEmail: matter.isSpecExcludedFromEmail ?? false,
            // A new case is never exempt, whatever the case it was started from.
            isAmlBlockingExempt: false,
            ...initialCustomFieldsObject,
        }),
        [
            classifiers,
            initialCustomFieldsObject,
            isOfficialRefNumberEnabled,
            isAdministratorFieldEnabled,
            canManagePayers,
            isSinglePracticeArea,
            sourcePricelists,
        ]
    );

    const isLoadingSourceMatterData =
        sourceMatterId !== null &&
        (isFetchingSourceMatter ||
            isFetchingSourcePricelists ||
            isLoadingActivePricedEmployees ||
            (!sourceMatterData && !isSourceMatterError));

    // Lock initialData once async deps resolve. Render-time guarded setState
    // (https://react.dev/reference/react/useState#storing-information-from-previous-renders)
    // — `!initialData` prevents the loop so RTK Query refetches don't reset the in-progress add form.
    // `classifiers` is an empty object while the list loads, so locking before it arrives would freeze the
    // classifier-backed fields (the carried-over status among them) as nulls.
    if (
        !initialData &&
        !isLoadingClassifiers &&
        !isLoadingCustomFieldData &&
        !(contactId && isLoadingContact) &&
        !isLoadingCurrentEmployee &&
        !isLoadingCurrentUserOffice &&
        !isLoadingSourceMatterData &&
        !isFetchingTenantConfiguration &&
        !(isHandledByOfficeControlEnabled && isLoadingPrimaryOffice)
    ) {
        setInitialData(sourceMatter ? getInitialDataFromSourceMatter(sourceMatter) : getInitialData());
    }

    if (
        isLoadingClassifiers ||
        (contactId && isLoadingContact) ||
        isLoadingCurrentEmployee ||
        isLoadingCurrentUserOffice ||
        isLoadingSourceMatterData ||
        isFetchingTenantConfiguration ||
        (isHandledByOfficeControlEnabled && isLoadingPrimaryOffice) ||
        initialData === undefined ||
        isLoadingCustomFieldData
    ) {
        return <Loader />;
    }

    return (
        <MatterPageForm
            type="add"
            initialData={initialData}
            classifiers={classifiers}
            cancelRedirectPath={sourceMatter ? `/matter/${sourceMatter.id}` : undefined}
            inactiveEmployeeIds={inactiveEmployeeIds}
        />
    );
};

export default MatterPageAdd;
